import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import Button from "components/button";
import Icon from "components/icon";
import Select from "components/select";
import Typography from "components/typography";
import { useAppTranslation } from "locale/useAppTranslation";

interface ExportOption {
  label: string;
  value: string;
}

interface ExportSidePanelProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  fileFormatOptions: ExportOption[];
  exportTypeOptions: ExportOption[];
  fileFormatValue?: string;
  exportTypeValue?: string;
  onFileFormatChange: (value: string) => void;
  onExportTypeChange: (value: string) => void;
  title?: string;
  subtitle?: string;
  submitLabel?: string;
  submitDisabled?: boolean;
  submitLoading?: boolean;
  widthClassName?: string;
  testIdPrefix?: string;
}

const ExportSidePanel: React.FC<ExportSidePanelProps> = ({
  open,
  onClose,
  onSubmit,
  fileFormatOptions,
  exportTypeOptions,
  fileFormatValue = "",
  exportTypeValue = "",
  onFileFormatChange,
  onExportTypeChange,
  title = "Export Data",
  subtitle = "Download your data easily in your preferred format",
  submitLabel = "Export",
  submitDisabled,
  submitLoading = false,
  widthClassName = "max-w-[360px] md:max-w-[420px]",
  testIdPrefix = "export-side-panel",
}) => {
  const { t } = useAppTranslation("general");
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (typeof document === "undefined") {
    return null;
  }

  const isSubmitDisabled = submitDisabled ?? (!fileFormatValue || !exportTypeValue);

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[1000] bg-black/30 transition-opacity duration-300 ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={onClose}
        aria-hidden="true"
        data-testid={`${testIdPrefix}-backdrop`}
      />

      <aside
        className={`fixed right-0 top-0 z-[1001] flex h-screen w-full ${widthClassName} transform flex-col overflow-hidden rounded-l-[24px] bg-white transition-transform duration-300 ease-out ${
          open ? "translate-x-0 shadow-[0_24px_80px_rgba(15,23,42,0.18)]" : "translate-x-full"
        }`}
        data-testid={testIdPrefix}
        aria-hidden={!open}
      >
        <div className="border-b border-border px-6 pb-5 pt-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Typography variant="heading5" as="h2">
                {title}
              </Typography>
              <Typography variant="body" tone="muted" className="mt-2 max-w-[260px]">
                {subtitle}
              </Typography>
            </div>

            <Button
              type="button"
              variant="outline"
              color="tertiary"
              size="small"
              className="h-8 w-8 rounded-full border-0 p-0 text-text-secondary hover:bg-greyScale-90"
              onClick={onClose}
              aria-label="Close"
              data-testid={`${testIdPrefix}-close`}
            >
              <Icon name="close-2" size={18} />
            </Button>
          </div>
        </div>

        <div className="flex-1 px-6 py-6">
          <div className="space-y-4">
            <Select
              label={t("export-file-format")}
              required
              options={fileFormatOptions}
              value={fileFormatValue}
              onValueChange={(value) => onFileFormatChange(value as string)}
              placeholder={t("export-select-placeholder")}
              reserveHelperSpace={false}
              data-testid="select-export-format"
            />

            <Select
              label={t("export-type")}
              required
              options={exportTypeOptions}
              value={exportTypeValue}
              onValueChange={(value) => onExportTypeChange(value as string)}
              placeholder={t("export-select-placeholder")}
              reserveHelperSpace={false}
              data-testid="select-export-scope"
            />
          </div>
        </div>

        <div className="px-6 py-4">
          <Button
            type="button"
            color="primary"
            size="medium"
            isFullSize
            onClick={onSubmit}
            disabled={isSubmitDisabled || submitLoading}
            data-testid="btn-confirm-download"
          >
            {submitLoading ? t("export-exporting") : submitLabel}
          </Button>
        </div>
      </aside>
    </>,
    document.body,
  );
};

export default ExportSidePanel;